import { CommandBar, DetailsList, Panel, PanelType, Spinner, Selection, SelectionMode, MessageBar, MessageBarType, ActionButton, IColumn } from "office-ui-fabric-react"
import { useEffect, useState } from "react";
import { Route, RouteComponentProps, useHistory } from "react-router";
import { deleteLocation, loadLocations } from "../../../api/siteslocations";
import { FloorplansPanel } from "./FloorplansPanel";
import { LocationFormPanel } from "../forms/LocationFormPanel";
import { ILocation } from "../../../types/SiteLocation.types"
import { RoomsPanel } from "./RoomsPanel";

export interface ILocationsPanelProps {
    onDismiss: () => void;
    siteId: number;
}

export const LocationsPanel = (props: ILocationsPanelProps) => {
    const { siteId, onDismiss } = props;
    const history = useHistory();

    const [ locations, setLocations ] = useState<Partial<ILocation>[]>();
    const [ error, setError ] = useState<string>();
    const [ loading, setLoading ] = useState(false);
    const [ locationSelected, setLocationSelected ] = useState(false);

    const onSelectionChanged = () => {
        const selectedItems = selection.getSelection() as ILocation[];
        if (selectedItems !== undefined && selectedItems.length > 0) {
            setLocationSelected(true);
        } else {
            setLocationSelected(false);
        }
    };
    
    const [ selection ] = useState(new Selection({ onSelectionChanged }));

    const _loadLocations = async () => {
        setLoading(true);
        selection.setAllSelected(false);
        setLocationSelected(false);
        const locationsResponse = await loadLocations(props.siteId);
        if (locationsResponse.ok) {
            setLocations(await locationsResponse.json());
        } else {
            setError("Unable to load locations: " + locationsResponse.statusText);
        }
        setLoading(false);
    };

    const _deleteLocation = async (id: number) => {
        const deleteResponse = await deleteLocation(id);
        if (deleteResponse.ok) {
            _loadLocations();
        } else {
            setError("Unable to delete location: " + deleteResponse.statusText)
        }
    }

    useEffect(() => {
        _loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Panel 
            headerText="Locations" 
            isOpen={true} 
            onDismiss={onDismiss}
            type={PanelType.medium} 
        >
            { error !== undefined && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}
            <p><strong>Create or edit on-site locations</strong></p>
            <p>
                Locations might be buildings, annexes or floors within buildings; any area you can create a floorplan for.
            </p>
            <CommandBar
                items={[
                    {
                        key: "new",
                        text: "New",
                        iconProps: { iconName: "Add" },
                        onClick: () => history.push(`/menu/settings/sites/${siteId}/locations/new`)
                    },
                    {
                        disabled: !locationSelected,
                        key: "edit",
                        text: "Edit",
                        iconProps: { iconName: "Edit" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as ILocation[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                history.push(`/menu/settings/sites/${siteId}/locations/${selectedItem.Id}/edit`);
                            }
                        }
                    },
                    {
                        disabled: !locationSelected,
                        key: "delete",
                        text: "Delete",
                        iconProps: { iconName: "Delete" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as ILocation[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                if (window.confirm("Are you sure? Note: Please make sure there are no bookings active for this location before deleting.")) {
                                    _deleteLocation(selectedItem.Id);
                                }
                            }
                        }
                    }
                ]}
            />
            { loading === true && (
                <Spinner />
            )}
            { loading === false && locations !== undefined && locations.length > 0 && (
                <DetailsList
                    items={locations}
                    columns={[
                        {
                            key: "name",
                            fieldName: "Title",
                            name: "Name",
                            minWidth: 40
                        },
                        {
                            key: "editRooms",
                            fieldName: "ID",
                            name: "Rooms",
                            minWidth: 80,
                            onRender: (item: ILocation, index?: number, column?: IColumn) => {
                                return <ActionButton styles={{flexContainer: { alignItems: "flex-start" }}} text="Rooms" iconProps={{iconName: "Room"}} onClick={() => {
                                    history.push(`/menu/settings/sites/${item.SiteId}/locations/${item.Id}/rooms`);
                                }} />
                            }
                        },
                        {
                            key: "editFloorplans",
                            fieldName: "ID",
                            name: "Floorplans",
                            minWidth: 130,
                            onRender: (item: ILocation, index?: number, column?: IColumn) => {
                                return <ActionButton styles={{flexContainer: { alignItems: "flex-start" }}} text="Desk Floorplans" iconProps={{iconName: "Nav2DMapView"}} onClick={() => {
                                    history.push(`/menu/settings/sites/${item.SiteId}/locations/${item.Id}/floorplans`);
                                }} />
                            }
                        },
                        
                    ]}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                />
            )}
            <Route path="/menu/settings/sites/:siteId/locations/:locationId/rooms" component={(route: RouteComponentProps<{siteId?: string|undefined, locationId?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.siteId)) && !Number.isNaN(Number(route.match.params.locationId))) {
                    return <RoomsPanel siteId={Number(route.match.params.siteId)} locationId={Number(route.match.params.locationId)} onDismiss={() => history.goBack()} />;
                }
                return null;
            }} />
            <Route path="/menu/settings/sites/:siteId/locations/:locationId/floorplans" component={(route: RouteComponentProps<{locationId?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.locationId))) {
                    return <FloorplansPanel locationId={Number(route.match.params.locationId)} onDismiss={() => history.goBack()} />;
                }
                return null;
            }} />
            <Route path="/menu/settings/sites/:siteId/locations/new" exact={true} component={(route: RouteComponentProps<{siteId?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.siteId))) {
                    return <LocationFormPanel location={{ SiteId: Number(route.match.params.siteId)}} onDismiss={(reloadLocations) => {
                        history.goBack();
                        if (reloadLocations) {
                            _loadLocations();
                        }
                    }} />;
                }
                return null;
            }} />
            <Route path="/menu/settings/sites/:siteId/locations/:locationId/edit" exact={true} component={(route: RouteComponentProps<{locationId?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.locationId))) {
                    const matchingSite = locations?.filter((l) => l.Id === Number(route.match.params.locationId))[0];
                    if (matchingSite) {
                        return <LocationFormPanel location={matchingSite} onDismiss={(reloadLocations) => {
                            history.goBack();
                            if (reloadLocations) {
                                _loadLocations();
                            }
                        }} />
                    }
                }
                return null;
            }} />
        </Panel>
    )
}